import { Box, SimpleGrid } from "@chakra-ui/react";
import {
  Children,
  Fragment,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";
import { type SummaryFieldProps } from "./SummaryField";

interface SummaryFieldGroupProps {
  children: ReactNode;
}

const isSummaryFieldElement = (
  child: ReactNode,
): child is ReactElement<SummaryFieldProps> =>
  isValidElement(child) &&
  // SummaryField sets displayName for this exact check
  (child.type as { displayName?: string }).displayName === "SummaryField";

// Children.toArray flattens arrays but NOT Fragments — call sites that wrap
// children in <>...</> would otherwise pass a single Fragment node here and
// defeat the bin-packer. Recurse into Fragments only; everything else passes
// through unchanged.
const flattenChildren = (children: ReactNode): ReactNode[] => {
  const out: ReactNode[] = [];
  for (const child of Children.toArray(children)) {
    if (isValidElement(child) && child.type === Fragment) {
      out.push(...flattenChildren((child.props as { children?: ReactNode }).children));
    } else {
      out.push(child);
    }
  }
  return out;
};

export const SummaryFieldGroup = ({ children }: SummaryFieldGroupProps) => {
  const rows: Array<{ kind: "pair" | "full"; nodes: ReactNode[] }> = [];
  let pendingHalf: ReactNode | null = null;
  let key = 0;

  const flushPending = () => {
    if (pendingHalf !== null) {
      // Orphan half emits a pair-row with an empty companion slot — keeps the
      // half-width column alignment consistent with adjacent paired rows.
      rows.push({ kind: "pair", nodes: [pendingHalf, null] });
      pendingHalf = null;
    }
  };

  for (const child of flattenChildren(children)) {
    if (isSummaryFieldElement(child) && child.props.span === "half") {
      if (pendingHalf === null) {
        pendingHalf = child;
      } else {
        rows.push({ kind: "pair", nodes: [pendingHalf, child] });
        pendingHalf = null;
      }
    } else {
      flushPending();
      rows.push({ kind: "full", nodes: [child] });
    }
  }
  flushPending();

  return (
    <Box>
      {rows.map((row, idx) => {
        if (row.kind === "pair") {
          return (
            <SimpleGrid
              key={`row-${idx}-${key++}`}
              data-testid="summary-field-group-pair"
              columns={2}
              spacing={{ base: 4, md: 7 }}
              borderTop={idx === 0 ? undefined : "1px solid"}
              borderColor="smBorder"
            >
              <Box>{row.nodes[0]}</Box>
              <Box>{row.nodes[1]}</Box>
            </SimpleGrid>
          );
        }
        return (
          <Box
            key={`row-${idx}-${key++}`}
            data-testid="summary-field-group-full"
            borderTop={idx === 0 ? undefined : "1px solid"}
            borderColor="smBorder"
          >
            {row.nodes[0]}
          </Box>
        );
      })}
    </Box>
  );
};
